import React, { useEffect, useState } from 'react';
import './SandclockAnimation.css';

interface SandclockAnimationProps {
    progress: number; // 0-100
}

const SandclockAnimation: React.FC<SandclockAnimationProps> = ({ progress }) => {
    const [particles, setParticles] = useState<number[]>([]);

    useEffect(() => {
        // Generate particles based on progress
        const particleCount = Math.floor((progress / 100) * 50);
        setParticles(Array.from({ length: particleCount }, (_, i) => i));
    }, [progress]);

    const topSandHeight = 100 - progress;
    const bottomSandHeight = progress;

    return (
        <div className="sandclock-container">
            <svg className="sandclock-svg" viewBox="0 0 200 320" xmlns="http://www.w3.org/2000/svg">
                <defs>
                    {/* Enhanced Gradients */}
                    <linearGradient id="glassGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="rgba(16, 185, 129, 0.15)" />
                        <stop offset="50%" stopColor="rgba(16, 185, 129, 0.08)" />
                        <stop offset="100%" stopColor="rgba(16, 185, 129, 0.2)" />
                    </linearGradient>

                    <linearGradient id="sandGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#10b981">
                            <animate attributeName="stopColor" values="#10b981;#34d399;#10b981" dur="3s" repeatCount="indefinite" />
                        </stop>
                        <stop offset="100%" stopColor="#34d399">
                            <animate attributeName="stopColor" values="#34d399;#6ee7b7;#34d399" dur="3s" repeatCount="indefinite" />
                        </stop>
                    </linearGradient>

                    <radialGradient id="sandGlow" cx="50%" cy="50%">
                        <stop offset="0%" stopColor="#10b981" stopOpacity="0.8" />
                        <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
                    </radialGradient>

                    {/* Enhanced Glow filter */}
                    <filter id="sandGlow">
                        <feGaussianBlur stdDeviation="4" result="coloredBlur" />
                        <feMerge>
                            <feMergeNode in="coloredBlur" />
                            <feMergeNode in="coloredBlur" />
                            <feMergeNode in="SourceGraphic" />
                        </feMerge>
                    </filter>

                    {/* Glass shine with animation */}
                    <linearGradient id="glassShine" x1="0%" y1="0%" x2="100%" y2="0%">
                        <stop offset="0%" stopColor="rgba(255,255,255,0)" />
                        <stop offset="50%" stopColor="rgba(255,255,255,0.4)">
                            <animate attributeName="stopColor" values="rgba(255,255,255,0.4);rgba(255,255,255,0.6);rgba(255,255,255,0.4)" dur="2s" repeatCount="indefinite" />
                        </stop>
                        <stop offset="100%" stopColor="rgba(255,255,255,0)" />
                    </linearGradient>

                    {/* Frame gradient */}
                    <linearGradient id="frameGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" stopColor="#10b981" stopOpacity="0.6" />
                        <stop offset="50%" stopColor="#34d399" stopOpacity="0.4" />
                        <stop offset="100%" stopColor="#10b981" stopOpacity="0.6" />
                    </linearGradient>
                </defs>

                {/* Outer glow effect */}
                <ellipse cx="100" cy="160" rx="90" ry="140" fill="url(#sandGlow)" opacity="0.3">
                    <animate attributeName="opacity" values="0.2;0.4;0.2" dur="3s" repeatCount="indefinite" />
                </ellipse>

                {/* Glass frame with enhanced styling */}
                <g className="glass-frame">
                    {/* Top bulb */}
                    <path
                        d="M 45 15 L 45 90 L 100 145 L 155 90 L 155 15 Z"
                        fill="url(#glassGradient)"
                        stroke="url(#frameGradient)"
                        strokeWidth="2.5"
                        opacity="0.4"
                    >
                        <animate attributeName="opacity" values="0.3;0.5;0.3" dur="4s" repeatCount="indefinite" />
                    </path>

                    {/* Bottom bulb */}
                    <path
                        d="M 45 305 L 45 230 L 100 175 L 155 230 L 155 305 Z"
                        fill="url(#glassGradient)"
                        stroke="url(#frameGradient)"
                        strokeWidth="2.5"
                        opacity="0.4"
                    >
                        <animate attributeName="opacity" values="0.3;0.5;0.3" dur="4s" repeatCount="indefinite" />
                    </path>

                    {/* Glass shine effect - left side */}
                    <rect
                        x="42"
                        y="15"
                        width="20"
                        height="290"
                        fill="url(#glassShine)"
                        opacity="0.5"
                        rx="2"
                    />

                    {/* Glass shine effect - right side */}
                    <rect
                        x="138"
                        y="15"
                        width="20"
                        height="290"
                        fill="url(#glassShine)"
                        opacity="0.3"
                        rx="2"
                    />
                </g>

                {/* Top sand (depleting) */}
                <g className="top-sand">
                    <clipPath id="topBulbClip">
                        <path d="M 45 15 L 45 90 L 100 145 L 155 90 L 155 15 Z" />
                    </clipPath>

                    <rect
                        x="45"
                        y={15 + (100 - topSandHeight)}
                        width="110"
                        height={topSandHeight}
                        fill="url(#sandGradient)"
                        filter="url(#sandGlow)"
                        clipPath="url(#topBulbClip)"
                        opacity="0.95"
                    >
                        <animate
                            attributeName="y"
                            from="15"
                            to={15 + (100 - topSandHeight)}
                            dur="0.8s"
                            fill="freeze"
                        />
                        <animate
                            attributeName="height"
                            from="100"
                            to={topSandHeight}
                            dur="0.8s"
                            fill="freeze"
                        />
                    </rect>

                    {/* Sand surface ripple effect */}
                    {topSandHeight > 0 && (
                        <ellipse
                            cx="100"
                            cy={15 + (100 - topSandHeight)}
                            rx="50"
                            ry="3"
                            fill="#34d399"
                            opacity="0.6"
                            clipPath="url(#topBulbClip)"
                        >
                            <animate attributeName="opacity" values="0.4;0.7;0.4" dur="2s" repeatCount="indefinite" />
                        </ellipse>
                    )}
                </g>

                {/* Enhanced falling sand particles */}
                <g className="falling-particles">
                    {progress > 0 && progress < 100 && (
                        <>
                            {[...Array(8)].map((_, i) => (
                                <g key={i}>
                                    <circle
                                        cx={98 + (i % 3 - 1) * 2}
                                        cy="145"
                                        r="2.5"
                                        fill="#10b981"
                                        opacity="0"
                                        filter="url(#sandGlow)"
                                    >
                                        <animate
                                            attributeName="cy"
                                            from="145"
                                            to="175"
                                            dur={`${0.4 + i * 0.08}s`}
                                            repeatCount="indefinite"
                                        />
                                        <animate
                                            attributeName="opacity"
                                            from="0.9"
                                            to="0"
                                            dur={`${0.4 + i * 0.08}s`}
                                            repeatCount="indefinite"
                                        />
                                        <animate
                                            attributeName="r"
                                            from="2.5"
                                            to="1"
                                            dur={`${0.4 + i * 0.08}s`}
                                            repeatCount="indefinite"
                                        />
                                    </circle>
                                </g>
                            ))}
                        </>
                    )}
                </g>

                {/* Bottom sand (filling) */}
                <g className="bottom-sand">
                    <clipPath id="bottomBulbClip">
                        <path d="M 45 305 L 45 230 L 100 175 L 155 230 L 155 305 Z" />
                    </clipPath>

                    <rect
                        x="45"
                        y={305 - bottomSandHeight}
                        width="110"
                        height={bottomSandHeight}
                        fill="url(#sandGradient)"
                        filter="url(#sandGlow)"
                        clipPath="url(#bottomBulbClip)"
                        opacity="0.95"
                    >
                        <animate
                            attributeName="y"
                            from="305"
                            to={305 - bottomSandHeight}
                            dur="0.8s"
                            fill="freeze"
                        />
                        <animate
                            attributeName="height"
                            from="0"
                            to={bottomSandHeight}
                            dur="0.8s"
                            fill="freeze"
                        />
                    </rect>

                    {/* Sand surface with glow */}
                    {bottomSandHeight > 0 && (
                        <ellipse
                            cx="100"
                            cy={305 - bottomSandHeight}
                            rx="50"
                            ry="3"
                            fill="#34d399"
                            opacity="0.7"
                            clipPath="url(#bottomBulbClip)"
                        >
                            <animate attributeName="opacity" values="0.5;0.8;0.5" dur="2s" repeatCount="indefinite" />
                        </ellipse>
                    )}

                    {/* Enhanced ripple effect when sand lands */}
                    {progress > 0 && progress < 100 && (
                        <>
                            <circle
                                cx="100"
                                cy={305 - bottomSandHeight}
                                r="5"
                                fill="none"
                                stroke="#10b981"
                                strokeWidth="1.5"
                                opacity="0"
                            >
                                <animate
                                    attributeName="r"
                                    from="5"
                                    to="35"
                                    dur="1.2s"
                                    repeatCount="indefinite"
                                />
                                <animate
                                    attributeName="opacity"
                                    from="0.7"
                                    to="0"
                                    dur="1.2s"
                                    repeatCount="indefinite"
                                />
                                <animate
                                    attributeName="strokeWidth"
                                    from="1.5"
                                    to="0.5"
                                    dur="1.2s"
                                    repeatCount="indefinite"
                                />
                            </circle>
                            <circle
                                cx="100"
                                cy={305 - bottomSandHeight}
                                r="5"
                                fill="none"
                                stroke="#34d399"
                                strokeWidth="1"
                                opacity="0"
                            >
                                <animate
                                    attributeName="r"
                                    from="5"
                                    to="25"
                                    dur="1s"
                                    repeatCount="indefinite"
                                />
                                <animate
                                    attributeName="opacity"
                                    from="0.5"
                                    to="0"
                                    dur="1s"
                                    repeatCount="indefinite"
                                />
                            </circle>
                        </>
                    )}
                </g>

                {/* Enhanced neck glow with pulsing effect */}
                <ellipse
                    cx="100"
                    cy="160"
                    rx="10"
                    ry="5"
                    fill="#10b981"
                    opacity={progress > 0 && progress < 100 ? "0.7" : "0"}
                    filter="url(#sandGlow)"
                >
                    <animate
                        attributeName="opacity"
                        values="0.4;0.9;0.4"
                        dur="0.6s"
                        repeatCount="indefinite"
                    />
                    <animate
                        attributeName="rx"
                        values="10;12;10"
                        dur="0.6s"
                        repeatCount="indefinite"
                    />
                </ellipse>

                {/* Decorative frame corners */}
                <g opacity="0.6">
                    <circle cx="45" cy="15" r="3" fill="#10b981" />
                    <circle cx="155" cy="15" r="3" fill="#10b981" />
                    <circle cx="45" cy="305" r="3" fill="#10b981" />
                    <circle cx="155" cy="305" r="3" fill="#10b981" />
                </g>
            </svg>

            {/* Enhanced progress percentage */}
            <div className="sandclock-progress-text">
                <span className="progress-number">{Math.round(progress)}</span>
                <span className="progress-percent">%</span>
            </div>
        </div>
    );
};

export default SandclockAnimation;
